import { string2hex } from "@pixi/utils";
import { Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, ParticleEmitterAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { View } from "@westpierstudio/luma/mvc";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { GameServices } from "game/GameServices";
import { Point } from "pixi.js";
import { CoreSymbolID, Win } from "servertypes/SharedTypes";
import { ReelsLayout } from "../reels/ReelsModel";
import { JackpotParticle } from "./JackpotParticle";
import { JackpotRect } from "./JackpotRect";

export interface JackpotSpec extends AssetSpec {
    colours: string[][]
    alpha: number
    tweenDuration: number
    particleTrail: ParticleEmitterAssetSpec
    particleOffset: Point
}

export class Jackpot extends View implements Resettable {
    private readonly spec: JackpotSpec;
    private rects: JackpotRect[][] = [];
    private particles: JackpotParticle[] = [];

    readonly COLOURS: number[][] = [];

    constructor(protected override readonly services: GameServices, protected readonly reelSpec: ReelsLayout) {
        super(services);

        const { factory, resources } = services;

        this.spec = resources.getSpec(`jackpot`);
        this.COLOURS = this.spec.colours.map((arr) => arr.map((str) => string2hex(str)));

        this.setup();
    }

    private setup() {
        this.setupRects();
        this.setupParticles();
    }

    private setupParticles() {
        const { positions } = this.reelSpec;
        for (let i = 0; i < positions.length; i++) {
            const particle1 = new JackpotParticle(this.services, this.spec, this.reelSpec);
            particle1.x = positions[i].x;
            this.addChild(particle1);
            this.particles.push(particle1);
            const particle2 = new JackpotParticle(this.services, this.spec, this.reelSpec, true);
            particle2.x = positions[i].x;
            this.addChild(particle2);
            this.particles.push(particle2);
        }
    }

    private setupRects() {
        const { symbolSize, symbolCount, positions } = this.reelSpec;
        for (let i = 0; i < positions.length; i++) {
            const pos = positions[i];
            const colour = i % 2 == 0 ? this.COLOURS[0] : this.COLOURS[1];

            const rect1 = new JackpotRect(symbolSize, pos, colour, this.spec);
            const rect2 = new JackpotRect(symbolSize, pos, colour, this.spec);
            rect1.anchor.y = 0;
            rect1.position.y = -symbolSize.height * 2;
            rect2.anchor.y = 1;
            rect2.position.y = symbolSize.height * 2;

            this.rects.push([rect1, rect2]);
            this.addChild(rect1);
            this.addChild(rect2);
        }
    }

    private resizeRects(win: { column: number; row: number; }[]) {
        const { symbolSize } = this.reelSpec;
        for (const symbol of win) {
            const col = this.rects[symbol.column];
            col[0].height = symbol.row * symbolSize.height;
            col[1].height = (3 - symbol.row) * symbolSize.height;
        }
    }

    startJackpot(wins: Win[]) {
        const jackpotWin = wins.find((win) => win.symbol == CoreSymbolID.LO);
        if (jackpotWin) {
            this.beginOnPattern(jackpotWin.pattern);
        }
    }

    beginOnPattern(jackpotWin: { column: number, row: number }[]) {
        this.visible = true;
        this.resizeRects(jackpotWin);
        this.startAnimateRects();
        this.startParticleEffect();
    }

    stopJackpot() {
        this.stopAnimateRects();
        this.stopParticleEffect();
        Tween.get(this).wait(this.spec.tweenDuration).call(() => this.visible = false);
    }

    private startAnimateRects() {
        for (let i = 0; i < this.rects.length; i++) {
            const rectuple = this.rects[i];
            for (const rect of rectuple) {
                rect.startAnimation();
            }
        }
    }

    private stopAnimateRects() {
        for (let i = 0; i < this.rects.length; i++) {
            const rectuple = this.rects[i];
            for (const rect of rectuple) {
                rect.stopAnimation();
            }
        }
    }

    private startParticleEffect() {
        for (const particle of this.particles) {
            particle.startParticles();
        }
    }

    private stopParticleEffect() {
        for (const particle of this.particles) {
            particle.stopParticles();
        }
    }

    override reset(): void {
        this.stopParticleEffect();
        this.stopAnimateRects();
        this.visible = false;
    }
}

